import Loader from "components/Loaders/Loader";
import AuditLogEntryMapper from "components/Mappers/AuditLogEntryMapper";
import AlertMessage from "components/Message/AlertMessage";
import { fetchHandleGetAuditLogs } from "helpers/Audit/AuditHelpers";
import { TokenContext } from "helpers/constants/Contexts";
import { AuditOperationCategories } from "helpers/enums/AuditOperationCategories";
import { IAuditLog } from "interfaces/Audit/IAuditLog";
import { useCallback, useContext, useEffect, useState } from "react";
import { Modal, Tab, Table, Tabs } from "react-bootstrap";
import { useDispatch } from "react-redux";

// Define proper TypeScript interface for props
interface OrganisationAuditWindowProps {
  show: boolean;
  handleClose: () => void;
  organisation: {
    organisationId: string;
    organisationName: string;
  };
}

// Define interface for the API state
interface AuditLogsState {
  data: IAuditLog[];
  loading: boolean;
  error: string | null;
}

const OrganisationAuditWindow: React.FC<OrganisationAuditWindowProps> = ({
  show = false,
  handleClose,
  organisation,
}) => {
  const dispatch = useDispatch();
  const accessToken = useContext(TokenContext);
  // Combined state management for better organization
  const [auditLogsState, setAuditLogsState] = useState<AuditLogsState>({
    data: [],
    loading: true,
    error: null,
  });

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const onGetAuditLogsSuccess = useCallback((logs: IAuditLog[]) => {
    setAuditLogsState({
      data: logs,
      loading: false,
      error: null,
    });
  }, []);

  const onGetAuditLogsFailure = useCallback((message: string) => {
    setAuditLogsState({
      data: [],
      loading: false,
      error: message,
    });
  }, []);

  const getAuditLogs = useCallback(
    (entityId: string) => {
      dispatch(
        fetchHandleGetAuditLogs(
          accessToken,
          entityId,
          (logs) => onGetAuditLogsSuccess(logs),
          (message) => onGetAuditLogsFailure(message)
        )
      );
    },
    [dispatch, accessToken, onGetAuditLogsSuccess, onGetAuditLogsFailure]
  );

  useEffect(() => {
    if (show) {
      // Reset loading state when modal opens
      setAuditLogsState((state) => ({ ...state, loading: true, error: null }));
      getAuditLogs(organisation.organisationId);
    }
  }, [show, organisation.organisationId, getAuditLogs]);

  // Extracted table components for better readability
  const renderDetailsTable = () => (
    <div className="overflow-x-auto bg-white rounded">
      <Table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Email</th>
            <th className="p-3 text-left">Description</th>
            <th className="p-3 text-left">Changed by</th>
            <th className="p-3 text-left">Date of change</th>
            <th className="p-3 text-left">
              {"Time "}
              <span className="font-light text-sm">{`(${timezone})`}</span>
            </th>
            <th className="p-3 text-left"></th>
          </tr>
        </thead>
        <tbody>
          <AuditLogEntryMapper
            auditLogs={auditLogsState.data}
            operationCategories={[AuditOperationCategories.DetailsChange]}
          />
        </tbody>
      </Table>
    </div>
  );

  const renderStatusTable = () => (
    <div className="overflow-x-auto bg-white rounded">
      <Table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-3 text-left">Status</th>
            <th className="p-3 text-left">Changed by</th>
            <th className="p-3 text-left">Date of change</th>
            <th className="p-3 text-left">
              {"Time "}
              <span className="font-light text-sm">{`(${timezone})`}</span>
            </th>
            <th className="p-3 text-left"></th>
          </tr>
        </thead>
        <tbody>
          <AuditLogEntryMapper
            auditLogs={auditLogsState.data}
            operationCategories={[AuditOperationCategories.StatusChange]}
          />
        </tbody>
      </Table>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      className="audit-window"
    >
      <Modal.Header
        className="bg-grey-dark-2 text-white border-b-grey-light-2"
        closeButton
        closeVariant="white"
      >
        <Modal.Title className="w-full text-center font-bold">
          Organisation History
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-5 bg-grey-light">
        {auditLogsState.loading ? (
          <Loader maxHeight={300} />
        ) : auditLogsState.error ? (
          <AlertMessage
            success={false}
            title={"Error Loading Audit Logs"}
            message={auditLogsState.error}
          />
        ) : (
          <div className="flex flex-col gap-4 ">
            <div className="bg-white rounded-md p-4 shadow-sm">
              <div className="flex items-center">
                <svg
                  className="w-6 h-6 mr-2 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <h4 className="font-bold text-xl mb-0">
                  {organisation.organisationName}
                </h4>
              </div>
            </div>
            <Tabs defaultActiveKey="details" variant="pills" data-color="black">
              <Tab eventKey="details" title="Details">
                {renderDetailsTable()}
              </Tab>
              <Tab eventKey="status" title="Status">
                {renderStatusTable()}
              </Tab>
            </Tabs>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default OrganisationAuditWindow;
